import { useRef, useState } from 'react';
import { COUNTRIES, LANGUAGES, SPORTS } from '@rabbitfuel/shared';
import { BACKDROP_PRESET, COVER_PRESET, LOGO_PRESET, fileToDataUrl, type ImagePreset } from '../lib/images.ts';
import { toLocalInput } from '../lib/format.ts';

export interface EventFormValues {
  title: string;
  club: string;
  sport: string;
  discipline: string;
  location: string;
  country: string;
  language: string;
  startsAt: string;
  description: string;
}

export const emptyEventForm: EventFormValues = {
  title: '',
  club: '',
  sport: 'laufen',
  discipline: '',
  location: '',
  country: 'DE',
  language: 'de',
  startsAt: toLocalInput(Date.now() + 3600_000),
  description: '',
};

export function EventFields({
  values,
  onChange,
}: {
  values: EventFormValues;
  onChange: (patch: Partial<EventFormValues>) => void;
}) {
  return (
    <>
      <div className="field">
        <label htmlFor="title">Titel</label>
        <input
          id="title"
          className="input"
          value={values.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Elbufer 10K Herbstlauf"
          required
        />
      </div>

      <div className="field">
        <label htmlFor="club">Verein</label>
        <input
          id="club"
          className="input"
          value={values.club}
          onChange={(e) => onChange({ club: e.target.value })}
          placeholder="Sportfreunde Lauftreff Elbufer"
          required
        />
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="sport">Sportart</label>
          <select
            id="sport"
            className="select"
            value={values.sport}
            onChange={(e) => onChange({ sport: e.target.value })}
          >
            {SPORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.icon} {s.label}
              </option>
            ))}
          </select>
          <span className="hint">Danach wird auf der Startseite gefiltert.</span>
        </div>
        <div className="field">
          <label htmlFor="discipline">Disziplin</label>
          <input
            id="discipline"
            className="input"
            value={values.discipline}
            onChange={(e) => onChange({ discipline: e.target.value })}
            placeholder="Straßenlauf · 10 km"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="startsAt">Start</label>
          <input
            id="startsAt"
            className="input"
            type="datetime-local"
            value={values.startsAt}
            onChange={(e) => onChange({ startsAt: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="location">Ort</label>
          <input
            id="location"
            className="input"
            value={values.location}
            onChange={(e) => onChange({ location: e.target.value })}
            placeholder="Elbdeich, Hamburg"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="country">Land</label>
          <select
            id="country"
            className="select"
            value={values.country}
            onChange={(e) => onChange({ country: e.target.value })}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="language">Stream-Sprache</label>
          <select
            id="language"
            className="select"
            value={values.language}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <span className="hint">In welcher Sprache kommentiert und gechattet wird.</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor="description">Beschreibung</label>
        <textarea
          id="description"
          className="input"
          style={{ minHeight: 130, fontFamily: 'inherit', fontSize: 15, resize: 'vertical' }}
          value={values.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={'Was passiert, wo stehen die Kameras?\n\nLeerzeile trennt Absätze — das steht später auf der Event-Seite.'}
        />
        <span className="hint">Das lesen Zuschauer:innen, bevor sie in den Stream gehen.</span>
      </div>
    </>
  );
}

/** Bildauswahl mit Vorschau. Verkleinert im Browser, bevor etwas hochgeht. */
export function ImagePicker({
  label,
  hint,
  preset,
  value,
  currentUrl,
  shape = 'wide',
  onPick,
}: {
  label: string;
  hint: string;
  preset: ImagePreset;
  /** Neu gewaehltes Bild als data:-URL, null = loeschen, undefined = unveraendert. */
  value: string | null | undefined;
  currentUrl?: string | null;
  shape?: 'wide' | 'square';
  onPick: (dataUrl: string | null | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const preview = value === null ? null : (value ?? currentUrl ?? null);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      onPick(await fileToDataUrl(file, preset));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bild konnte nicht verarbeitet werden.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="field">
      <label>{label}</label>
      <div className={`imagepicker imagepicker--${shape}`}>
        <div className="imagepicker__preview">
          {preview ? <img src={preview} alt="" /> : <span>kein Bild</span>}
        </div>
        <div className="imagepicker__actions">
          <p className="hint">{hint}</p>
          <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn--sm btn--quiet"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? 'verarbeite …' : preview ? 'Ersetzen' : 'Bild wählen'}
            </button>
            {preview && (
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => onPick(null)}>
                Entfernen
              </button>
            )}
          </div>
          {error && <span className="hint" style={{ color: 'var(--red-700)' }}>{error}</span>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void choose(e.target.files?.[0])}
      />
    </div>
  );
}

export { BACKDROP_PRESET, COVER_PRESET, LOGO_PRESET };
